import React, { useCallback, useState } from 'react';
import { toast } from 'react-toastify';

import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';

import UmbrellaSVG from 'Components/Admin/assets/UmbrellaSVG';
import RiseSVG from 'Components/Admin/assets/RiseSVG';
import TermSVG from 'Components/Admin/assets/TermSVG';

import Table from 'Components/Admin/Table';
import Tooltip from 'Components/Admin/Tooltip';
import Autocomplete from '../../../../../../components/Admin/Autocomplete/Autocomplete';
import Checkbox from 'Components/Checkbox';
import ActionPopup from 'Components/Admin/ActionPopup';
import formatPrice from 'Shared/util/formatPrice';
import getCurrencySymbol from 'Shared/util/getCurrencySymbol';
import LottieTable from '../../../../components/LottieTable/LottieTable';

import { buttons } from './buttonsForActionPopup';
import { queryGetContracts, queryGetEmployees, queryGetPositions } from './queries';
import { ADMIN_URL } from '../../../../../Admin/constants/constants';
import { alphabeticalSorting } from 'Components/Admin/utils';
import { useSelectWithQueryOptions, useSelectWithQueryOptionsLazy } from 'Shared/hooks/useSelectWithQueryOptions';
import { StickyColumnType } from 'Components/Admin/Table/components/TableComponent/TableComponent';
import getIdIfExist from 'Shared/util/getIdIfExist';

const PROJECTS_MIN_COLUMN_WIDTH = 200;

const EmployeesTable = ({ employeesFilter, filterMeta: { isChanged }, onFilterChange }) => {
  const {
    options: positionOptions,
    currentOption: position,
  } = useSelectWithQueryOptions(queryGetPositions, 'positions', employeesFilter.position);

  const {
    options: projectOptions,
    currentOption: project,
    getDataLazy: getDataLazyProject,
    refetchLazy: refetchLazyProject,
  } = useSelectWithQueryOptionsLazy(queryGetContracts, 'contracts', employeesFilter.project);

  const handleProjectChange = (project) => onFilterChange({ project });
  const handlePositionChange = (position) => onFilterChange({ position });

  const getQueryVariables = ({ project, position, ...variables }) => ({
    ...variables,
    project: getIdIfExist(project),
    position: getIdIfExist(position),
  });

  const { data: dataEmployees, loading, error: errorEmployees } = useQuery(queryGetEmployees, {
    options: { cache: 'cache-only' },
    variables: getQueryVariables(employeesFilter),
  });

  const renderedEmployees = dataEmployees?.employees;

  const [selected, setSelected] = useState([]);
  const [currentPage, setCurrentPage] = useState(null);
  const [currentPageSize, setCurrentPageSize] = useState(null);
  const currentPageEmployeesIDs = renderedEmployees
    ? renderedEmployees.slice(currentPage * currentPageSize, currentPageSize).map((i) => +i.user.id)
    : [];

  const handleSelectEmployee = useCallback(({ target: { value, checked } }) => {
    const id = parseInt(value);
    if (checked) {
      setSelected((prevState) => {
        let arr = [...prevState];
        arr.push(id);
        return arr;
      });
    } else {
      setSelected((prevState) => {
        return [...prevState].filter((item) => item !== id);
      });
    }
  }, []);

  const handleAllSelects = ({ target: { checked } }) => {
    if (checked) {
      setSelected(currentPageEmployeesIDs);
    } else {
      setSelected([]);
    }
  };

  const handleCleanSelects = () => {
    setSelected([]);
  };

  const renderLinksForAssignments = (assignments) =>
    assignments.map(({ clientContract }, i, arr) => (
      <Link key={clientContract.id} to={`/${ADMIN_URL}/companies/${clientContract.id}/change`} className="table__link">
        {clientContract.title || 'Null'}
        {arr.length > 1 && arr.length - 1 !== i && ', '}
      </Link>
    ));

  const mapEmployeesDataToRows = useCallback(
    (employees = []) => {
      const filteredEmployees = employees.map(
        (
          {
            user: { id, firstName, lastName },
            position,
            assignments,
            salary,
            vacationDaysLeft,
            vacationAutoApproval,
            sinceStart,
            lastRise,
          },
          index,
        ) => {
          return {
            select: {
              title: (
                <Checkbox
                  checked={selected.indexOf(+id) !== -1}
                  value={id.toString()}
                  onChange={(e) => handleSelectEmployee(e)}
                />
              ),
            },
            employee: {
              title: (
                <Link to={`/${ADMIN_URL}/employee/${id}/change`} className="table__link">
                  {firstName || '-'} {lastName || '-'}
                </Link>
              ),
            },
            position: {
              title: position ? position.title : '-',
              id: position ? position.id : '-',
            },
            contract: {
              title: renderLinksForAssignments(assignments),
            },
            salary: {
              title: salary ? `${getCurrencySymbol(salary.currency)}${formatPrice(salary.amount)}` : '-',
            },
            info: {
              title: (
                <div className="table__info-cell">
                  <Tooltip
                    type="small"
                    direction={index === 0 ? 'bottom' : 'top'}
                    body="vacation"
                    header="Vacation"
                    content={vacationAutoApproval && 'Auto approval'}
                  >
                    <span className={`table__info-box ${vacationAutoApproval ? 'positive' : 'def'}`}>
                      <UmbrellaSVG />
                      {vacationDaysLeft}
                    </span>
                  </Tooltip>
                  <Tooltip type="small" direction={index === 0 ? 'bottom' : 'top'} body="rise" header="Last rise">
                    <span className={`table__info-box ${'def'}`}>
                      <RiseSVG />
                      {lastRise}
                    </span>
                  </Tooltip>
                  <Tooltip type="small" direction="left" body="term" header="Employed term">
                    <span className={`table__info-box ${'def'}`}>
                      <TermSVG />
                      {sinceStart}
                    </span>
                  </Tooltip>
                </div>
              ),
            },
          };
        },
      );

      return filteredEmployees.length
        ? filteredEmployees
        : [
          {
            not_found: {
              title: 'No contacts with this name',
            },
            employee: { title: 'No employees with this params' },
          },
        ];
    },
    [handleSelectEmployee, selected],
  );

  const columns = [
    {
      cellProps: {
        Header: (
          <Checkbox
            checked={selected.length === currentPageEmployeesIDs.length && currentPageEmployeesIDs.length > 0}
            onChange={(e) => handleAllSelects(e)}
          />
        ),
        headerClassName: 'rt-th-select sticky-column sticky-column_left',
        accessor: 'select',
        sortable: false,
        width: 32,
        className: 'sticky-column sticky-column_left',
      },
    },
    {
      title: 'Employee',
      cellProps: {
        accessor: 'employee',
        sortMethod: alphabeticalSorting,
        minWidth: PROJECTS_MIN_COLUMN_WIDTH,
      },
    },
    {
      title: 'Position',
      cellProps: {
        Header: (
          <Autocomplete
            titleCell="Position"
            options={positionOptions}
            option={position}
            getItem={handlePositionChange}
          />
        ),
        Cell: (props) => {
          return (
            <>
              <span className="rt-td__head">Position 222</span>
              <div className={'rt-td__cell'}>
                <div>
                  <span className="colleague-name">{props.row.position?.title}</span>
                </div>
              </div>
            </>
          );
        },
        accessor: 'position',
        sortable: false,
        minWidth: PROJECTS_MIN_COLUMN_WIDTH,
      },
    },
    {
      title: 'Contract',
      cellProps: {
        Header: (
          <Autocomplete
            titleCell="Contract"
            options={projectOptions}
            option={project}
            getItem={handleProjectChange}
            refetch={refetchLazyProject}
            onMenuOpen={() => getDataLazyProject({ term: '' })}
          />
        ),
        Cell: (props) => {
          return (
            <>
              <span className="rt-td__head">Contract</span>
              <div className={'rt-td__cell'}>
                <div>
                  <span className="colleague-name">{props.row.contract?.title}</span>
                </div>
              </div>
            </>
          );
        },
        accessor: 'contract',
        sortable: false,
        minWidth: PROJECTS_MIN_COLUMN_WIDTH,
      },
    },
    {
      cellProps: {
        Header: <span className="salary-cell-header">Salary</span>,
        Cell: (props) => {
          return (
            <div className="salary-cell-body">
              <span className="rt-td__head">Salary</span>
              <div className={'rt-td__cell'}>
                <div data-testid="salary">
                  <span className="salary-cell-value">{props.row.salary?.title}</span>
                </div>
              </div>
            </div>
          );
        },
        accessor: 'salary',
        sortable: false,
        minWidth: PROJECTS_MIN_COLUMN_WIDTH,
      },
    },
    {
      cellProps: {
        accessor: 'info',
        sortable: false,
        minWidth: PROJECTS_MIN_COLUMN_WIDTH,
      },
    },
  ];

  const computeTableProps = useCallback(
    (employees = []) => {
      return {
        data: employees,
        columns: columns,
        className: 'admin_table',
        tableSettings: {
          hasHeader: true,
          paginationHeight: 20,
          headerHeight: 20,
          rowHeight: 20,
          tablePadding: 20,
          pageSize: window.innerWidth > 767 ? 10 : 3,
        },
      };
    },
    [columns],
  );

  if (errorEmployees) {
    toast(errorEmployees.message, {
      type: toast.TYPE.ERROR,
      autoClose: 3000,
      closeButton: true,
      hideProgressBar: false,
    });
  }

  return (
    <>
      {loading && <LottieTable dataTestId={'loading_employees'} />}
      <Table
        getTableProps={({ page, pageSize }) => {
          if (currentPage !== page) {
            setCurrentPage(page);
            setCurrentPageSize(pageSize);
            setSelected([]);
          }
          return {};
        }}
        key="employees"
        sortable={true}
        watchForResetPage={isChanged && employeesFilter}
        stickyColumn={StickyColumnType.FIRST}
        {...computeTableProps(mapEmployeesDataToRows(renderedEmployees))}
      />
      {selected.length ? (
        <ActionPopup buttons={buttons} selected={selected} handleCleanSelects={handleCleanSelects} />
      ) : null}
    </>
  );
};

export default EmployeesTable;
